"use client";

import { memo, useEffect } from "react";

import { GTM_ID } from "config/thirdParty";
import { useConsent } from "layouts/consent";

const GTM_SCRIPT_ID = "autotrack-gtm-script";

const MemoizedGtmProvider = memo(
    ({
        isConsentInteractionFinished,
        gtmId
    }: {
        isConsentInteractionFinished: boolean;
        gtmId: string | undefined;
    }) => {
        useEffect(() => {
            if (!isConsentInteractionFinished || !gtmId) {
                return;
            }

            if (typeof window === "undefined") {
                return;
            }

            const existingScript = document.getElementById(GTM_SCRIPT_ID);
            if (existingScript) {
                return;
            }

            window.dataLayer = window.dataLayer || [];
            window.dataLayer.push({
                "gtm.start": new Date().getTime(),
                "event": "gtm.js"
            });

            const script = document.createElement("script");
            script.id = GTM_SCRIPT_ID;
            script.async = true;
            script.src = `https://www.googletagmanager.com/gtm.js?id=${gtmId}`;
            document.head.appendChild(script);
        }, [isConsentInteractionFinished, gtmId]);

        return null;
    }
);

MemoizedGtmProvider.displayName = "MemoizedGtmProvider";

export const GtmProvider = (): JSX.Element => {
    const { isConsentInteractionFinished } = useConsent();

    return (
        <MemoizedGtmProvider
            gtmId={GTM_ID}
            isConsentInteractionFinished={isConsentInteractionFinished}
        />
    );
};
